/**
 * telemetry — the privacy-minimal client beacon to the ANL-PR1 ingest endpoints.
 *
 * Two fire-and-forget writers, both BEST-EFFORT and SILENT (telemetry must never
 * surface an error into the UI or block a render):
 *
 *   postSession({ activeMs, subject })  -> POST /telemetry/session  (204)
 *       Foreground active-time heartbeat. `subject` is the route's engagement-
 *       AREA tag from areaForPath (NEVER free user text). Omitted when the route
 *       is outside the taxonomy.
 *
 *   postPerf(samples)                   -> POST /telemetry/perf      (204)
 *       Batch of { metric, value } client-perf samples. ONLY metrics in the
 *       closed PERF_METRICS set are sent; anything else is dropped client-side so
 *       a forged/unknown metric never 400s the whole batch on the server.
 *
 * THE DISABLED LATCH (the load-bearing anti-spam rule)
 * ----------------------------------------------------
 * The ingest surface is flag-gated (404 when ANALYTICS_TELEMETRY_ENABLED is OFF)
 * and consent-gated for an under-13 without guardian consent (403 platform_minor
 * / 426 age_required on the session route). On ANY of those responses the beacon
 * has learned, for THIS page session, that it is either invisible or not allowed
 * — so it sets a module-level latch and STOPS sending. Continuing would spam a
 * 404/403 on every heartbeat for the whole session. The latch resets on a full
 * page load (module re-init), which is the right granularity: a flag flip or a
 * consent grant takes effect on the user's next visit.
 *
 * THE STALE-TOKEN LATCH (401 → quiet until the token changes)
 * -----------------------------------------------------------
 * A 401 means the token we HOLD is dead (expired 60-min JWT, revoked family, a
 * localStorage leftover while the login screen is showing) — not that telemetry
 * is off. Because these writers bypass axios (below), they never benefit from
 * the refresh-rotation interceptor, so retrying with the same token would 401
 * on every heartbeat/web-vital — console spam on logged-out pages. On a 401 we
 * remember the EXACT token that was rejected and skip sends while _token()
 * still returns it. The moment the token CHANGES (login, or a refresh rotation
 * persisting a new one) the comparison stops matching and telemetry resumes —
 * no reload needed. That is why a 401 must NOT trip the page-session _disabled
 * latch above: that one only clears on a full page load. (No token at all
 * never sends anything — see the no-token guards on both writers.)
 *
 * WHY a raw beacon, not the shared axios instance
 * -----------------------------------------------
 * These calls must NOT inherit the app's axios interceptors: a telemetry 401
 * must never auto-logout the student, and a telemetry 426/403 must never pop the
 * COPPA DOB modal / consent overlay in the user's face mid-lesson. So we use the
 * native fetch (and navigator.sendBeacon on unload) directly with the bearer
 * token read from localStorage — the same token source the rest of the app uses.
 * No axios import here is deliberate.
 *
 * UNLOAD FLUSH: on pagehide/visibility-hidden the page may be torn down before a
 * normal fetch resolves. We use fetch with `keepalive: true` on EVERY path incl.
 * unload — the documented modern replacement for navigator.sendBeacon that,
 * crucially, CAN set the Authorization header. We do NOT use sendBeacon: the
 * backend auth is header-only (HTTPBearer), so a sendBeacon `?access_token=`
 * query param would both LEAK a minor's JWT into server access logs AND fail to
 * authenticate (401 — the sample lost). keepalive lets the POST outlive the page
 * with the token in the HEADER, never the URL.
 */

import { API_URL } from '../config';
import { PERF_METRICS } from '../telemetry/perfMetrics';

const SESSION_PATH = '/api/v1/telemetry/session';
const PERF_PATH = '/api/v1/telemetry/perf';

// Module-level latch. Once true, both writers no-op for the rest of the page
// session. Set by a 404 (flag OFF) or a 403/426 (under-13 not allowed) response.
let _disabled = false;

// The stale-token latch: the exact bearer token the server last 401'd. While
// _token() still returns this value both writers skip — the token is dead and
// these writers never auto-refresh it. A login (or refresh rotation) persists
// a NEW token, the comparison stops matching, and telemetry resumes. Unlike
// _disabled this is NOT for-the-page-session: it must clear on re-login.
let _rejectedToken = null;

/** True once the beacon has latched off (flag OFF or consent-gated). */
export function isTelemetryDisabled() {
  return _disabled;
}

/**
 * Latch the beacon off for the rest of the page session. Exported so the unload
 * path and tests can trip it; normally tripped internally by a gate response.
 */
export function disableTelemetry() {
  _disabled = true;
}

/**
 * TEST-ONLY: reset the latch between test cases. Not used in app code (a real
 * page session never un-latches — that's the whole point). Named with the
 * __test__ prefix so its single purpose is unmistakable at call sites.
 */
export function __resetTelemetryForTests() {
  _disabled = false;
  _rejectedToken = null;
}

/** The bearer token the app persisted at login (same source as axios default). */
function _token() {
  try {
    return localStorage.getItem('token') || '';
  } catch {
    // Some privacy modes throw on localStorage access — treat as no token.
    return '';
  }
}

/**
 * A gate response is one that means "stop beaconing this session":
 *   404 — the feature flag is OFF (surface invisible).
 *   403 — under-13 platform_minor: engagement write frozen (limited mode).
 *   426 — age_required: the session route needs a DOB the student hasn't given.
 * (The perf route is user-less and only 404s, but treating 403/426 uniformly is
 *  harmless and keeps one latch rule.)
 */
function _isGateStatus(status) {
  return status === 404 || status === 403 || status === 426;
}

/**
 * POST a foreground active-time heartbeat. Fire-and-forget: resolves to a small
 * result object and NEVER rejects. Skips entirely when latched off, when there's
 * no auth token (unauthenticated users are never beaconed) or the held token is
 * one the server already 401'd (stale-token latch), or when activeMs is
 * not a positive finite number AND there's no session to count. We always send
 * when there's a token + the beacon is live, because a 0-ms flush still COUNTS
 * as a session server-side (sessions_count++), but we guard against NaN/negative.
 *
 * @param {{activeMs:number, subject?:string}} args
 * @param {{useBeacon?:boolean}} [opts]  useBeacon=true → navigator.sendBeacon
 *   (the unload path); default false → fetch with keepalive.
 */
export async function postSession({ activeMs, subject } = {}, opts = {}) {
  if (_disabled) return { skipped: true, reason: 'disabled' };
  const token = _token();
  if (!token) return { skipped: true, reason: 'no-token' };
  if (token === _rejectedToken) return { skipped: true, reason: 'auth-gated' };

  // Coerce to a non-negative integer ms. A non-finite / negative value becomes
  // 0 (still a valid "session happened" beacon) rather than a 422 on the server.
  let ms = Number(activeMs);
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  ms = Math.floor(ms);

  const body = { active_ms: ms };
  // Only attach `subject` when it's a non-empty area tag. areaForPath returns
  // undefined outside the taxonomy → we omit it (the session still counts).
  if (typeof subject === 'string' && subject.length > 0) {
    body.subject = subject;
  }

  // The unload path (opts.useBeacon) and the in-page path are BOTH keepalive
  // fetch with the bearer header now (see _postUnload) — keepalive is what makes
  // the unload POST survive teardown, not sendBeacon. Kept as a distinct call so
  // the heartbeat hook's unload intent stays legible at the call site.
  if (opts.useBeacon) {
    return _postUnload(SESSION_PATH, body);
  }
  return _postJson(SESSION_PATH, body);
}

/**
 * POST a batch of client-perf samples. Fire-and-forget; NEVER rejects. Filters
 * to PERF_METRICS-valid samples CLIENT-SIDE (an unknown metric would 400 the
 * whole batch server-side, so we drop it here). Skips when latched off, when
 * there's no token (or only one the server already 401'd — stale-token latch),
 * or when nothing valid remains after filtering. Caps the
 * batch to the server's per-call limit so an oversized array can't 422/400.
 *
 * @param {Array<{metric:string, value:number, context?:string}>} samples
 * @param {{useBeacon?:boolean}} [opts]
 */
export async function postPerf(samples, opts = {}) {
  if (_disabled) return { skipped: true, reason: 'disabled' };
  const token = _token();
  if (!token) return { skipped: true, reason: 'no-token' };
  if (token === _rejectedToken) return { skipped: true, reason: 'auth-gated' };

  const clean = sanitizePerfSamples(samples);
  if (clean.length === 0) return { skipped: true, reason: 'no-valid-samples' };

  const body = { samples: clean };
  if (opts.useBeacon) {
    return _postUnload(PERF_PATH, body);
  }
  return _postJson(PERF_PATH, body);
}

// Server caps a perf batch at 50 (schemas._PERF_MAX_SAMPLES_PER_CALL); stay
// under it so a large client batch is trimmed rather than rejected wholesale.
const MAX_PERF_SAMPLES = 50;
const MAX_CONTEXT_LEN = 64; // schemas.PerfSample.context max_length

/**
 * Keep only valid perf samples: `metric` in the closed PERF_METRICS set and
 * `value` a finite non-negative number. Drops everything else (unknown metric,
 * NaN/inf/negative value) so the batch can't be rejected by the server. `context`
 * is optional + bounded; it is NOT stored server-side but we keep it tidy.
 * Trims to MAX_PERF_SAMPLES. Total over any input — returns [] on junk.
 */
export function sanitizePerfSamples(samples) {
  if (!Array.isArray(samples)) return [];
  const out = [];
  for (const s of samples) {
    if (!s || typeof s !== 'object') continue;
    if (!PERF_METRICS.has(s.metric)) continue;
    const v = Number(s.value);
    if (!Number.isFinite(v) || v < 0) continue;
    const sample = { metric: s.metric, value: v };
    if (typeof s.context === 'string' && s.context.trim()) {
      sample.context = s.context.trim().slice(0, MAX_CONTEXT_LEN);
    }
    out.push(sample);
    if (out.length >= MAX_PERF_SAMPLES) break;
  }
  return out;
}

/**
 * Fetch-based POST (the in-page path). Sends JSON with the bearer header and
 * `keepalive: true` so an in-flight beacon survives a soon-after navigation.
 * Swallows ALL errors. On a gate status (404/403/426) it trips the latch so the
 * beacon stops for the session; on a 401 it latches the rejected TOKEN so sends
 * stay quiet until the token changes (login / refresh rotation).
 */
async function _postJson(path, body) {
  const url = `${API_URL}${path}`;
  // Read the token ONCE for both the header and the 401 latch: if a refresh
  // rotation swaps tokens mid-send we must latch the token we actually sent,
  // never the fresh one (latching the fresh one would wrongly mute telemetry).
  const token = _token();
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      keepalive: true,
      // Telemetry is not a credentialed cross-site concern; the bearer header
      // carries auth. Omit cookies to keep the request cheap + CORS-simple.
      credentials: 'omit',
    });
    if (res && _isGateStatus(res.status)) {
      _disabled = true;
      return { skipped: false, gated: true, status: res.status };
    }
    if (res && res.status === 401) {
      // Dead token (expired / revoked / stale localStorage leftover on the
      // login screen). Latch THIS token so we go quiet instead of 401-spamming
      // every heartbeat — deliberately NOT the page-session _disabled latch,
      // because a fresh login must resume telemetry without a reload.
      _rejectedToken = token;
      return { skipped: false, authGated: true, status: 401 };
    }
    return { skipped: false, status: res ? res.status : 0 };
  } catch {
    // Network error / offline / aborted unload — silently ignored. We do NOT
    // latch on a transient network failure (only on an explicit gate status):
    // a flaky connection should not permanently kill telemetry for the session.
    return { skipped: false, error: true };
  }
}

/**
 * The UNLOAD-path writer. Deliberately NOT navigator.sendBeacon — sendBeacon
 * cannot set an Authorization header, which would force the bearer token into a
 * `?access_token=` URL param that (a) leaks a minor's JWT into server access
 * logs and (b) doesn't even authenticate (the backend auth is header-only, so
 * the query param is ignored → 401 → the sample is lost). Instead we use a
 * keepalive fetch — the modern sendBeacon replacement that CAN set headers and
 * still outlives the page on pagehide/visibility-hidden. So the unload path is
 * identical to the in-page path: token in the HEADER, never the URL. (Both
 * latches still learn here — a gate status trips _disabled, a 401 latches the
 * rejected token — since this is a real fetch with a readable response, a
 * small bonus over a fire-into-the-void beacon.)
 */
function _postUnload(path, body) {
  return _postJson(path, body);
}
